import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  ConfirmationMessageModal,
  EmailsTable,
  FullScreenLoader,
  MenuItem,
  ScrollToTop,
  TableActionsMenu,
  PrintLayout,
} from '@Components/index';
import { CommonConstants } from '@Constants/index';
import EmailsModal from '@Containers/Emails/EmailsModal';
import { LibraryModal } from '@Containers/Groups';
import { MetaData } from '@Models/common.models';
import { EmailsQuery } from '@Models/emails.models';
import {
  useArchiveEmailsMutation,
  useDeleteEmailsMutation,
  useGetEmailsListsQuery,
} from '@Services/email.service';
import {
  BaseAlertProps,
  ConfirmationActionTypes,
  DialerCrudTypes,
  DialerTypes,
} from '@Typings/global.types';
import {
  getModalBtnComponent,
  getFilters,
  GetRows,
  getRTKQuery,
  getResponseData,
  getConfirmationMessageNode,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import usePrint from '@Utils/hooks/usePrint.hook';

const EmailsActive: FunctionComponent = (): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { triggerAlert } = useAlert();
  const { componentRef, handlePrint, setPrintState } = usePrint(true);

  /** STATES */
  const [checked, setChecked] = useState<readonly string[]>([]);
  const [modalCrudType, setModalCrudType] = useState<DialerCrudTypes | null>(
    null
  );
  const [emailId, setEmailId] = useState<string>('');
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [itemsCount, setItemsCount] = useState<number>(0);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationActionType, setConfirmationActionType] =
    useState<ConfirmationActionTypes | null>(null);
  const [isBulkAction, setIsBulkAction] = useState(false);
  const [showLibraryModal, setShowLibraryModal] = useState<boolean>(false);

  const emailsActiveQuery: EmailsQuery = useMemo(
    () => getRTKQuery(location.search),
    [location.search]
  );

  /** RTK QUERY HOOK */
  const {
    data,
    isLoading,
    isFetching,
    error: getEmailsError,
  } = useGetEmailsListsQuery({
    data: emailsActiveQuery,
  });
  const [archiveEmails, archiveMethods] = useArchiveEmailsMutation();
  const [deleteEmails, deleteMethods] = useDeleteEmailsMutation();

  /** TABLE LOADING STATE */
  const loading =
    isLoading ||
    isFetching ||
    archiveMethods.isLoading ||
    deleteMethods.isLoading;

  /** EMAIL DATA `DEFAULT` KEY VALUE */
  const emailDefaultType = getResponseData(data?.list, emailId)?.default;

  // show error for get query
  useEffect(() => {
    if (getEmailsError) {
      triggerAlert(getEmailsError, setAlertState);
    }
  }, [getEmailsError, triggerAlert]);

  /** UPDATES THE CHECKED STATE AFTER BULK ACTIONS */
  useEffect(() => {
    setEmailId('');
    setIsBulkAction(false);
    setItemsCount(0);

    if (archiveMethods.isSuccess) {
      setChecked([]);
    }

    if (deleteMethods.isSuccess) {
      setChecked([]);
    }
  }, [archiveMethods.isSuccess, deleteMethods.isSuccess]);

  /** TRIGGERS SUCCESS OR ERROR ALERT AFTER ACTIONS */
  useEffect(() => {
    // ARCHIVE SUCCESS
    if (archiveMethods.isSuccess) {
      switch (archiveMethods.data.alert) {
        case CommonConstants.GLOBAL_SUCCESS_CODES.DEFAULT_RESOURCES_IGNORED:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.DEFAULT_EMAILS_IGNORED_ARCHIVE,
            setAlertState
          );
          break;
        default:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.ARCHIVE_EMAIL,
            setAlertState
          );
      }
    }

    // ARCHIVE ERROR
    if (archiveMethods.error) {
      triggerAlert(archiveMethods.error, setAlertState);
    }
  }, [
    triggerAlert,
    archiveMethods.data,
    archiveMethods.isSuccess,
    archiveMethods.error,
  ]);

  useEffect(() => {
    // DELETE SUCCESS
    if (deleteMethods.isSuccess) {
      switch (deleteMethods.data.alert) {
        case CommonConstants.GLOBAL_SUCCESS_CODES.DEFAULT_RESOURCES_IGNORED:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.DEFAULT_EMAILS_IGNORED_DELETE,
            setAlertState
          );
          break;
        default:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.DELETE_EMAIL,
            setAlertState
          );
      }
    }

    // DELETE ERROR
    if (deleteMethods.error) {
      triggerAlert(deleteMethods.error, setAlertState);
    }
  }, [
    triggerAlert,
    deleteMethods.data,
    deleteMethods.isSuccess,
    deleteMethods.error,
  ]);

  /** EMAILS ACTION HANDLERS --> */
  /** HANDLES EMAILS ARCHIVE */
  const handleArchive = (ids: readonly string[]) => {
    const userId = query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) || '';
    const metaData: MetaData = getFilters(query.parse());

    return itemsCount
      ? archiveEmails({
          ...metaData,
          archive: true,
          itemsCount,
          userId: `${userId}`,
        })
      : archiveEmails({ ids, archive: true, userId: `${userId}` });
  };
  /** HANDLES EMAILS DELETE */
  const handleDelete = (ids?: readonly string[]) => {
    const userId = query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) || '';
    const metaData: MetaData = getFilters(query.parse());

    return itemsCount
      ? deleteEmails({ ...metaData, itemsCount, userId: `${userId}` })
      : deleteEmails({ ids, userId: `${userId}` });
  };

  /** HANDLES EMAIL ADD */
  const handleEmailAdd = () => {
    setModalCrudType(DialerCrudTypes.Add);
    setEmailId('');
  };

  const handleLibraryAdd = () => {
    setShowLibraryModal(true);
  };
  /** <-- EMAILS ACTION HANDLERS */

  /** Table Modal Button Component Function
   *
   * Returns the Modal Button component which opens modal when clicked.
   */
  const modalBtnComponent = (id: string, name: string): JSX.Element => {
    const handleClick = () => {
      setModalCrudType(DialerCrudTypes.View);
      setEmailId(id);
    };

    return getModalBtnComponent({
      id,
      name,
      onClick: handleClick,
    });
  };

  /** Table Actions Component Function
   *
   * Returns the table actions component with required menu items.
   */
  const actionsComponent = (
    id: string,
    disabled?: boolean,
    defaultType?: boolean
  ): JSX.Element => {
    /** Handles click of action menu items */
    const handleTableActionsClick = (
      confirmationType: ConfirmationActionTypes
    ) => {
      setIsConfirmationModalOpen(true);
      setConfirmationActionType(confirmationType);
    };

    /** Actions Menu Data */
    const actionsMenu: MenuItem[] = [
      {
        label: t('common.messages.Edit'),
        icon: 'edit',
        value: id,
        onClick: () => {
          setModalCrudType(DialerCrudTypes.Edit);
          setEmailId(id);
        },
      },
      {
        label: t('common.messages.Delete'),
        icon: 'delete',
        value: id,
        onClick: () => {
          setItemsCount(0);
          setIsBulkAction(false);
          setEmailId(id);
          handleTableActionsClick(ConfirmationActionTypes.Delete);
        },
        disabled: defaultType,
      },
      {
        label: t('common.messages.Print'),
        icon: 'print',
        value: id,
        onClick: () => {
          setPrintState(true);
          if (emailId === id) {
            handlePrint();
          } else {
            setEmailId(id);
          }
        },
      },
      {
        label: t('common.messages.Archive'),
        icon: 'sendAndArchive',
        value: id,
        onClick: () => {
          setItemsCount(0);
          setIsBulkAction(false);
          setEmailId(id);
          handleTableActionsClick(ConfirmationActionTypes.Archive);
        },
      },
    ];

    return <TableActionsMenu menuItems={actionsMenu} disabled={disabled} />;
  };

  /** Rows, Total Rows Count */
  const rows = GetRows(data?.list, actionsComponent, modalBtnComponent);
  const totalRowsCount = data?.meta.count
    ? parseInt(data.meta.count, 10)
    : CommonConstants.INITIAL_ROWS_PER_PAGE;

  /** Handle Bulk Count
   * Callback to update ItemsCount
   */
  const handleBulk = (count: number) => setItemsCount(count);

  /** Handles Bulk action button click */
  const handleBulkActionBtnClick = (
    confirmationType: ConfirmationActionTypes
  ) => {
    setConfirmationActionType(confirmationType);
    setIsBulkAction(true);
    setIsConfirmationModalOpen(true);
  };

  /** CALLS PRINT FUNCTION WHEN EMAIL ID IS SET (PRINT TRIGGERED FROM ACTIONS MENU) */
  useEffect(() => {
    if (emailId) {
      handlePrint();
    }
  }, [emailId, handlePrint]);

  /** Handles confirmation click of modal and performs action accordingly */
  const handleConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    if (confirmationActionType === ConfirmationActionTypes.Delete) {
      return isBulkAction ? handleDelete(checked) : handleDelete([emailId]);
    }
    if (confirmationActionType === ConfirmationActionTypes.Archive) {
      return isBulkAction ? handleArchive(checked) : handleArchive([emailId]);
    }
  };

  /** Reset states when confirmation modal is closed */
  const onConfirmationModalClose = () => {
    setEmailId('');
  };

  return !isLoading ? (
    <>
      <ScrollToTop
        dependencies={[
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) || '',
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) || '',
        ]}
      />
      <PrintLayout
        ref={componentRef}
        printData={{
          name: getResponseData(data?.list, emailId)?.name,
          listType: getResponseData(data?.list, emailId)?.type,
          isDefault: getResponseData(data?.list, emailId)?.default,
          subject: getResponseData(data?.list, emailId)?.subject,
          body: getResponseData(data?.list, emailId)?.message,
        }}
      />
      <EmailsModal
        emailId={emailId}
        setEmailId={setEmailId}
        crudType={modalCrudType}
        setCrudType={setModalCrudType}
        deleteBtnOnClick={() => handleDelete([emailId])}
      />
      <LibraryModal
        isOpen={showLibraryModal}
        setIsOpen={setShowLibraryModal}
        type="global_emails"
      />
      <ConfirmationMessageModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={getConfirmationMessageNode(
          confirmationActionType,
          emailDefaultType,
          getResponseData(data?.list, emailId)?.type
        )}
        headerText={
          emailDefaultType ? t('common.confirmationModal.Attention') : ''
        }
        cancelButtonText={
          emailDefaultType ? t('common.confirmationModal.Close') : ''
        }
        onClick={!emailDefaultType ? handleConfirmationModalClick : undefined}
        onClose={onConfirmationModalClose}
      />
      <EmailsTable
        heading={query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) && <></>}
        tabChild={!!query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID)}
        selectedBtnNumber={itemsCount || checked.length}
        alertState={alertState}
        EmailTemplateType={DialerTypes.Active}
        archiveUnarchiveBtnProps={{
          onClick: () => {
            handleBulkActionBtnClick(ConfirmationActionTypes.Archive);
          },
          disabled: loading,
        }}
        deleteBtnProps={{
          onClick: () => {
            handleBulkActionBtnClick(ConfirmationActionTypes.Delete);
          },
          disabled: loading,
        }}
        emailsAddBtnProps={{
          onClick: handleEmailAdd,
        }}
        libraryAddBtnProps={{
          onClick: handleLibraryAdd,
        }}
        tableProps={{
          loading,
          rows,
          checked,
          setChecked,
          paginationProps: {
            count: totalRowsCount,
          },
        }}
        handleBulk={handleBulk}
      />
    </>
  ) : (
    <FullScreenLoader />
  );
};

export default EmailsActive;
